// Helpery do kalkulacji i zarządzania danymi noclegowymi

import {
  GuestStructure,
  AssignedGuestsSummary,
  AssignedRoom,
  AvailableRoom,
  ValidationResult,
  RoomSelection,
  AccommodationState,
  MinAvailabilityMap,
  AssignmentType,
} from "../types/accommodation.types";
import { BatchAvailabilityResponse } from "../../../types";

export class AccommodationHelpers {
  /**
   * Oblicza wymaganą liczbę gości na podstawie struktury
   */
  static calculateRequiredGuests(structure: GuestStructure): number {
    return structure.pairs * 2 + structure.singles + structure.singlesAlone;
  }

  /**
   * Oblicza przypisanych gości na podstawie listy pokoi
   */
  static calculateAssignedGuests(rooms: AssignedRoom[]): AssignedGuestsSummary {
    let pairs = 0;
    let singlesShared = 0;
    let singlesAlone = 0;

    // Filter only rooms that are actually assigned (have quantity > 0)
    const actuallyAssignedRooms = rooms.filter((room) => room.quantity > 0);

    actuallyAssignedRooms.forEach((room) => {
      const quantity = room.quantity || 0;

      switch (room.occupancyType) {
        case "pairs":
          pairs += quantity * 2;
          break;
        case "singles_shared":
          // Użyj personsCountPerRoom z bazy danych jeśli dostępne, inaczej fallback na personsPerRoom
          const personsInRoom =
            room.personsCountPerRoom || room.categoryOption.personsPerRoom || 1;
          singlesShared += quantity * personsInRoom;
          break;
        case "singles_alone":
          singlesAlone += quantity * 1;
          break;
      }
    });

    const totalAssigned = pairs + singlesShared + singlesAlone;

    return {
      pairs,
      singlesShared,
      singlesAlone,
      totalAssigned,
      totalRequired: totalAssigned, // Będzie nadpisane przez wywołującego
    };
  }

  /**
   * Oblicza pozostałych gości do przypisania
   */
  static getRemainingGuests(
    required: GuestStructure,
    assigned: AssignedGuestsSummary
  ): GuestStructure {
    return {
      pairs: Math.max(0, required.pairs * 2 - assigned.pairs) / 2,
      singles: Math.max(0, required.singles - assigned.singlesShared),
      singlesAlone: Math.max(0, required.singlesAlone - assigned.singlesAlone),
    };
  }

  /**
   * Oblicza dostępną liczbę pokoi danego typu (uwzględniając już używane)
   */
  static getAvailableRoomCount(
    room: AvailableRoom,
    assignedRooms: AssignedRoom[]
  ): number {
    const used = assignedRooms
      .filter((assigned) => assigned.categoryOptionId === room.categoryOptionId)
      .reduce((total, assigned) => total + (assigned.quantity || 0), 0);

    return Math.max(0, room.availableCount - used);
  }

  /**
   * Oblicza maksymalne użycie pokoju przez wszystkie dni (dla trybu MIN)
   */
  static getMaxUsedRoomCountAcrossDays(
    categoryOptionId: string,
    allDaysRooms: AssignedRoom[][]
  ): number {
    let maxUsed = 0;

    allDaysRooms.forEach((dayRooms) => {
      const usedOnThisDay = dayRooms
        .filter((room) => room.categoryOptionId === categoryOptionId)
        .reduce((total, room) => total + (room.quantity || 0), 0);

      maxUsed = Math.max(maxUsed, usedOnThisDay);
    });

    return maxUsed;
  }

  /**
   * Mapuje batch response na MIN availability map
   */
  static getMinAvailabilityAcrossDays(
    batchData: BatchAvailabilityResponse
  ): MinAvailabilityMap {
    if (!batchData?.minAvailability) {
      return {};
    }

    // minAvailability jest już wyliczone przez backend jako MIN ze wszystkich dni
    return batchData.minAvailability;
  }

  /**
   * Waliduje wybór pokoju przed dodaniem
   */
  static validateRoomSelection(
    selection: RoomSelection,
    availableRoom: AvailableRoom
  ): ValidationResult {
    if (!selection.quantity || selection.quantity <= 0) {
      return {
        isValid: false,
        error: "Liczba pokoi musi być większa od 0",
      };
    }

    if (selection.quantity > availableRoom.availableCount) {
      return {
        isValid: false,
        error: `Dostępne tylko ${availableRoom.availableCount} pokoi`,
        maxAllowed: availableRoom.availableCount,
      };
    }

    if (!selection.pricePerRoom || selection.pricePerRoom <= 0) {
      return {
        isValid: false,
        error: "Cena musi być większa od 0",
      };
    }

    // Walidacja dla singles_shared
    if (selection.assignmentType === "singles_shared") {
      const maxPersonsPerRoom = this.calculateMaxPersonsInRoom(availableRoom);
      if (
        selection.personsPerRoom &&
        selection.personsPerRoom > maxPersonsPerRoom
      ) {
        return {
          isValid: false,
          error: `Maksymalnie ${maxPersonsPerRoom} osób w tym pokoju`,
          maxAllowed: maxPersonsPerRoom,
        };
      }
    }

    return { isValid: true };
  }

  /**
   * Sprawdza czy można dodać pokój w obecnym stanie
   */
  static canAddRoom(
    selection: RoomSelection,
    currentState: AccommodationState
  ): boolean {
    // Sprawdź czy nie przekraczamy potrzeb gości
    const required = this.calculateRequiredGuests(currentState.guestStructure);
    const assigned = this.calculateAssignedGuests(currentState.assignedRooms);

    let additionalGuests = 0;
    switch (selection.assignmentType) {
      case "pairs":
        additionalGuests = selection.quantity * 2;
        break;
      case "singles_shared":
        additionalGuests = selection.quantity * (selection.personsPerRoom || 1);
        break;
      case "singles_alone":
        additionalGuests = selection.quantity * 1;
        break;
    }

    return assigned.totalAssigned + additionalGuests <= required;
  }

  /**
   * Oblicza maksymalną liczbę osób w pokoju na podstawie łóżek
   */
  static calculateMaxPersonsInRoom(room: AvailableRoom): number {
    return room.singleBeds + room.doubleBeds * 2 + room.convertibleBeds * 2;
  }

  /**
   * Oblicza sugerowaną cenę dla danego typu przypisania
   */
  static getSuggestedPrice(
    room: AvailableRoom,
    assignmentType: AssignmentType,
    date: string,
    personsCount?: number
  ): number {
    const pricing = room.pricing[date];
    if (!pricing) return 0;

    switch (assignmentType) {
      case "pairs":
        return pricing.double?.net || pricing.single?.net || 0;
      case "singles_shared":
        const actualPersons =
          personsCount || this.calculateMaxPersonsInRoom(room);
        if (actualPersons >= 3 && pricing.triple) {
          return pricing.triple.net;
        } else if (actualPersons >= 2 && pricing.double) {
          return pricing.double.net;
        } else {
          return pricing.single?.net || 0;
        }
      case "singles_alone":
        return pricing.single?.net || 0;
      default:
        return 0;
    }
  }

  /**
   * Formatuje datę do wyświetlania
   */
  static formatDate(dateString: string): string {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    const weekday = date.toLocaleDateString("pl-PL", { weekday: "short" });
    return `${day}.${month}.${year} (${weekday})`;
  }

  /**
   * Mapuje typ przypisania na etykietę wyświetlaną
   */
  static getAssignmentTypeLabel(type: AssignmentType): string {
    switch (type) {
      case "pairs":
        return "Pary";
      case "singles_shared":
        return "Single";
      case "singles_alone":
        return "Single (prywatnie)";
      default:
        return "Nieznany";
    }
  }

  /**
   * Mapuje typ przypisania na emoji/ikonę
   */
  static getAssignmentTypeIcon(type: AssignmentType): string {
    switch (type) {
      case "pairs":
        return "👫";
      case "singles_shared":
        return "👤";
      case "singles_alone":
        return "👤";
      default:
        return "❓";
    }
  }

  /**
   * Mapuje typ przypisania na kolor tła
   */
  static getAssignmentTypeColor(type: AssignmentType): string {
    switch (type) {
      case "pairs":
        return "bg-pink-50 border-pink-200";
      case "singles_shared":
        return "bg-blue-50 border-blue-200";
      case "singles_alone":
        return "bg-purple-50 border-purple-200";
      default:
        return "bg-gray-50 border-gray-200";
    }
  }

  /**
   * Sprawdza czy dzień jest weekendem (sobota)
   */
  static isWeekendDay(dateString: string): boolean {
    const date = new Date(dateString);
    return date.getDay() === 6; // Sobota
  }

  /**
   * Sprawdza czy pobyt to weekend (sobota-niedziela)
   */
  static isWeekendStay(checkInDate: string, checkOutDate: string): boolean {
    const checkIn = new Date(checkInDate);
    const checkOut = new Date(checkOutDate);
    const diffDays =
      (checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24);
    return diffDays === 1 && checkIn.getDay() === 6; // Sobota do niedzieli
  }

  /**
   * Sprawdza czy wszystkie dni mają identyczną strukturę gości
   */
  static areAllDayStructuresIdentical(guestStructurePerDay: {
    [dayId: string]: GuestStructure;
  }): boolean {
    const structures = Object.values(guestStructurePerDay);
    if (structures.length <= 1) return true;

    const first = structures[0];
    return structures.every(
      (s) =>
        s.pairs === first.pairs &&
        s.singles === first.singles &&
        s.singlesAlone === first.singlesAlone
    );
  }

  /**
   * Ekstraktuje przypisane pokoje z dni oferty - NOWA WERSJA: zwraca per-dzień
   */
  static extractAssignedRoomsFromDays(offerDays: any[]): {
    [dayId: string]: AssignedRoom[];
  } {
    const assignedRoomsPerDay: { [dayId: string]: AssignedRoom[] } = {};

    // Iteruj przez dni noclegowe (bez ostatniego dnia - checkout)
    const accommodationDays = offerDays.slice(0, -1);

    accommodationDays.forEach((day) => {
      const dayRooms: AssignedRoom[] = [];

      if (day.planItems) {
        day.planItems
          .filter(
            (item: any) => item.categoryOption?.optionType === "accommodation"
          )
          .forEach((item: any) => {
            dayRooms.push({
              id: item.id,
              categoryOptionId: item.categoryOption.id,
              categoryOption: {
                id: item.categoryOption.id,
                name: item.categoryOption.name,
                personsPerRoom: item.categoryOption.personsPerRoom || 1,
              },
              quantity: item.quantity || 0,
              unitPrice: parseFloat(item.unitPrice) || 0,
              occupancyType: item.occupancyType || "pairs",
              personsCountPerRoom: item.personsCountPerRoom,
              offerDayId: day.id,
              linkedItemId: item.linkedItemId,
            });
          });
      }

      // Dodaj pokoje tego dnia do mapy
      assignedRoomsPerDay[day.id] = dayRooms;
    });

    return assignedRoomsPerDay;
  }
}
